import Dinero from 'dinero.js';
import { isInteger, isNumber } from 'lodash/fp';
import { Currency, CurrencyCode } from '../../server-types';
import { countFractionDigits } from './helpers';
import { PRODUCT_QUANTITY_PRECISION } from './constants';

interface AddOrSubtract {
    precision?: number;
    mathOperation?: MathOperation;
}

export enum MathOperation {
    Add = 'Add',
    Substract = 'Substract',
}

type ExistingCurrency = CurrencyCode.CZK | CurrencyCode.EUR;

class PriceService {
    constructor(currency: CurrencyCode, locale: string) {
        Dinero.defaultCurrency = currency;
        Dinero.globalLocale = locale;
    }

    public assertInteger(int: number, intDescription: string) {
        if (!Number.isInteger(int)) {
            throw Error(`${intDescription} should be int number, received ${int}`);
        }
    }

    private assertPositiveNumber(int: number, intDescription: string) {
        if (int < 0) {
            throw Error(`${intDescription} should be postive, received ${int}`);
        }
    }

    private assertSupportedCurrency(currency: CurrencyCode) {
        if (!Object.values(CurrencyCode).includes(currency)) {
            throw Error(`${currency} is not supported`);
        }
    }

    private assertQuantity(quantity: number, { precision }: { precision: number}) {
        if (!isNumber(quantity)) {
            throw Error(`Quantity should be a number, received ${quantity} with type ${typeof quantity}`);
        }
        if (countFractionDigits(quantity) > precision) {
            throw Error(`Quantity precision should be less than ${precision}, received ${quantity}`);
        }
    }

    public roundWithPrecision(initialValue: number, targetPrecision: number) {
        const initialPrecision = countFractionDigits(initialValue);
        return Dinero({ amount: 10**initialPrecision, precision: initialPrecision })
            .multiply(initialValue)
            .convertPrecision(targetPrecision, 'HALF_AWAY_FROM_ZERO')
            .toUnit();
    }

    public multiply(
        price: number,
        quantity: number,
        currency?: CurrencyCode,
        { onlyPositiveQuantity = true }: { onlyPositiveQuantity?: boolean } = {}
    ): number {
        const currencyCode = currency || CurrencyCode[Dinero.defaultCurrency as ExistingCurrency];
        this.assertInteger(price, 'Price');
        this.assertPositiveNumber(price, 'Price');
        if (onlyPositiveQuantity) {
            this.assertPositiveNumber(quantity, 'Quantity');
        }
        this.assertSupportedCurrency(currencyCode);

        const currentPrice = Dinero({ amount: price, currency });
        const calculatedPrice = currentPrice.multiply(quantity);

        return calculatedPrice.getAmount();
    }

    public divide(price: number, quantity: number, currency?: CurrencyCode): number {
        const currencyCode = currency || CurrencyCode[Dinero.defaultCurrency as ExistingCurrency];
        this.assertInteger(price, 'Price');
        this.assertPositiveNumber(price, 'Price');
        this.assertPositiveNumber(quantity, 'Quantity');
        this.assertSupportedCurrency(currencyCode);

        const currentPrice = Dinero({ amount: price, currency });
        const calculatedPrice = currentPrice.divide(quantity);

        return calculatedPrice.getAmount();
    }

    public add(price: number, priceToAdd: number, currency?: CurrencyCode): number {
        const currencyCode = currency || CurrencyCode[Dinero.defaultCurrency as ExistingCurrency];
        this.assertInteger(priceToAdd, 'Price to sum with');
        this.assertInteger(price, 'Price');
        this.assertPositiveNumber(price, 'Price');
        this.assertPositiveNumber(priceToAdd, 'Quantity');
        this.assertSupportedCurrency(currencyCode);

        const currentPrice = Dinero({ amount: price, currency });
        const calculatedPrice = currentPrice.add(Dinero({ amount: priceToAdd, currency }));

        return calculatedPrice.getAmount();
    }

    public fractionToPercent(fraction: number, precision = 2) {
        const fractionPrecision = countFractionDigits(fraction);
        return Dinero({ amount: 10**fractionPrecision, precision: fractionPrecision })
            .multiply(fraction)
            .multiply(100)
            .convertPrecision(precision)
            .toUnit();
    }

    public sumQuantity(firstAddended: number, secondAddended: number) {
        return this.runArithmeticalOperation(firstAddended, secondAddended, { precision: PRODUCT_QUANTITY_PRECISION });
    }

    public subtractQuantity(initialValue: number, subtrahend: number) {
        return this.runArithmeticalOperation(
            initialValue,
            subtrahend,
            { precision: PRODUCT_QUANTITY_PRECISION, mathOperation: MathOperation.Substract }
        );
    }

    public multiplyQuantity(
        initialValue: number,
        multiplier: number,
        {
            precision = PRODUCT_QUANTITY_PRECISION,
            onlyPositiveInitialValue = true,
        }: { precision: number; onlyPositiveInitialValue?: boolean }) {
        this.assertQuantity(initialValue, { precision });
        this.assertQuantity(multiplier, { precision });
        if (onlyPositiveInitialValue) {
            this.assertPositiveNumber(initialValue, 'InitialValue');
        }

        this.assertPositiveNumber(multiplier, 'Multiplier');

        const initialValuePrecision = countFractionDigits(initialValue);
        const multiplierPrecision = countFractionDigits(multiplier);

        const initialValueAmount = Dinero({ amount: 10**initialValuePrecision, precision: initialValuePrecision })
            .multiply(initialValue)
            .convertPrecision(precision, 'HALF_AWAY_FROM_ZERO')
            .getAmount();
        const multiplierAmount = Dinero({ amount: 10**multiplierPrecision, precision: multiplierPrecision })
            .multiply(multiplier)
            .convertPrecision(precision, 'HALF_AWAY_FROM_ZERO')
            .getAmount();

        const finalPrecision = precision * 2;
        return Dinero({ amount: initialValueAmount, precision: finalPrecision })
            .multiply(multiplierAmount)
            .convertPrecision(precision, 'HALF_AWAY_FROM_ZERO')
            .toUnit();
    }

    public calculatePercentage(initialValue: number, divider: number, { precision = PRODUCT_QUANTITY_PRECISION }: { precision: number }) {
        this.assertQuantity(initialValue, { precision });
        this.assertQuantity(divider, { precision });
        this.assertPositiveNumber(initialValue, 'InitialValue');
        this.assertPositiveNumber(divider, 'Divider');
        const initialValuePrecision = countFractionDigits(initialValue);
        const multiplierPrecision = countFractionDigits(divider);

        const initialValueAmount = Dinero({ amount: 10**initialValuePrecision, precision: initialValuePrecision })
            .multiply(initialValue)
            .convertPrecision(precision + 4, 'HALF_AWAY_FROM_ZERO')
            .getAmount();

        const dividerInt = Dinero({ amount: 10**multiplierPrecision, precision: multiplierPrecision })
            .multiply(divider)
            .convertPrecision(precision, 'HALF_AWAY_FROM_ZERO')
            .getAmount();

        if (multiplierPrecision === 0) {
            return Dinero({ amount: initialValueAmount, precision: 2 })
                .divide(dividerInt)
                .convertPrecision(2)
                .toUnit();
        }

        return Dinero({ amount: 10**(initialValuePrecision + 4), precision: initialValuePrecision + 4 })
            .multiply(initialValue)
            .divide(divider)
            .multiply(100)
            .convertPrecision(2, 'HALF_AWAY_FROM_ZERO')
            .toUnit();
    }

    public percentageFrom(initialValue: number, percentage: number) {
        this.assertPositiveNumber(initialValue, 'Initial value');
        this.assertPositiveNumber(percentage, 'Percentage');
        const initialValuePrecision = countFractionDigits(initialValue);
        const percentagePrecision = countFractionDigits(percentage);

        if (percentagePrecision > 2) {
            throw new Error(`Percentage precision cannot be larger than 2, received ${percentagePrecision}`);
        }

        const initialValueAmount = Dinero({ amount: 10**initialValuePrecision, precision: initialValuePrecision })
            .multiply(initialValue)
            .convertPrecision(initialValuePrecision + 2, 'HALF_AWAY_FROM_ZERO')
            .getAmount();

        const percentageInt = Dinero({ amount: 10**percentagePrecision, precision: percentagePrecision })
            .multiply(percentage)
            .getAmount();

        return Dinero({ amount: initialValueAmount, precision: (initialValuePrecision + 2 + percentagePrecision) })
            .multiply(percentageInt)
            .divide(100)
            .convertPrecision(3, 'HALF_AWAY_FROM_ZERO')
            .toUnit();
    }

    private runArithmeticalOperation(
        initalValue: number,
        valueToAddOrSubtract: number,
        {
            precision = 2,
            mathOperation = MathOperation.Add,
        }: AddOrSubtract = {}) {
        if (isInteger(initalValue) && isInteger(valueToAddOrSubtract)) {
            if (mathOperation === MathOperation.Add) return initalValue + valueToAddOrSubtract;
            if (mathOperation === MathOperation.Substract) return initalValue - valueToAddOrSubtract;
        }

        if (countFractionDigits(initalValue) > precision ||
            countFractionDigits(valueToAddOrSubtract) > precision) {
            throw new Error(
                `Provided ${initalValue} or ${valueToAddOrSubtract} precision was more than ${precision}`);
        }

        const factor = 10 ** precision;
        const intFirstAddend = Math.round(initalValue * factor);
        const intSecondAddend = Math.round(valueToAddOrSubtract * factor);

        let result;
        if (mathOperation === MathOperation.Substract) result = intFirstAddend - intSecondAddend;
        else result = intFirstAddend + intSecondAddend;

        if (isInteger(result)) {
            return result / factor;
        }

        // We should rather use logger service to see values, not errors
        throw new Error(
            `Cant ${mathOperation} decimal numbers. Initial value $initalValue. Second value $valueToAddOrSubtract.
            Precision $precision`);
    }

    public toUnit(price: number, currency?: CurrencyCode): number {
        this.assertInteger(price, 'Price');
        return Dinero({ amount: price, currency }).toUnit();
    }

    public toCzechFiscalFormat(price: number) {
        this.assertInteger(price, 'Price');
        return Dinero({ amount: price, currency: CurrencyCode.CZK }).toFormat('0.00');
    }

    public toLocaleString(price: number, currency?: CurrencyCode) {
        /* This is needed to overcome -0 value being localized */
        const finalPrice = Object.is(price, -0) ? -price : price;
        this.assertInteger(finalPrice, 'Price');

        const finalCurrency = currency || CurrencyCode[Dinero.defaultCurrency as ExistingCurrency];
        return Dinero({ amount: finalPrice, currency: finalCurrency }).setLocale('cs-CZ').toFormat();
    }

    /* This will only be used on FE forms, to prepare data for transfer */
    public toInteger(price: number, currency?: CurrencyCode): number {
        const mockPrice = Dinero({ amount: 0, currency });
        const precision = mockPrice.getPrecision();


        return Dinero({ amount: 10**precision, precision })
            .multiply(price)
            .getAmount();
    }

    public parseIntegerFromFloat(number: number) {
        const precision = countFractionDigits(number);
        const amount = Dinero({ amount: 10**precision, precision })
            .multiply(number)
            .getAmount();

        return { amount, precision };
    }

    public parseIntegerFromFloatWithPrecision(number: number, precision: number) {
        const amount = Dinero({ amount: 10**precision, precision })
            .multiply(number)
            .getAmount();

        return amount;
    }

    public integerToFloatWithPrecision(amount: number, precision: number) {
        return Dinero({ amount, precision }).toUnit();
    }

    public getAmountWithPrecision(amount: number, precision: number, currency?: CurrencyCode) {
        const mockPrice = Dinero({ amount: 0, currency });
        const targetPrecision = mockPrice.getPrecision();
        return Dinero({ amount, precision }).convertPrecision(targetPrecision).getAmount();
    }

    public calculatePriceWithTax(priceWithoutTax: number, taxRate: number, { currencyCode, precision }: Currency) {
        this.assertInteger(priceWithoutTax, 'Price without tax');
        this.assertPositiveNumber(taxRate, 'Tax rate');

        const taxRatePrecision = countFractionDigits(taxRate);
        const initialPrice = Dinero({ amount: priceWithoutTax, currency: currencyCode, precision });
        const taxRateAsDinero = Dinero({ amount: 10**taxRatePrecision, precision: taxRatePrecision + 2 })
            .multiply(taxRate)
            .convertPrecision(2, 'HALF_AWAY_FROM_ZERO');

        const taxValue = initialPrice.multiply(taxRateAsDinero.toUnit(), 'HALF_AWAY_FROM_ZERO');
        const priceWithVat = initialPrice.add(taxValue);

        return { taxValue: taxValue.getAmount(), priceWithVat: priceWithVat.getAmount() };
    }

    public calculateTax(price: number, tax: number, { currencyCode, precision }: Currency) {

        this.assertInteger(price, 'Price');
        this.assertSupportedCurrency(currencyCode);
        const currentPrice = Dinero({ amount: price, currency: currencyCode, precision });

        const taxRate = Dinero({ amount: tax * 100, precision: 4 });
        const taxDiscriminator = Dinero({ amount: 1, precision: 0 }).add(taxRate);
        const taxValue = currentPrice
            .subtract(
                currentPrice.divide(taxDiscriminator.toUnit(), 'HALF_AWAY_FROM_ZERO')
            );
        const priceWithoutTax = currentPrice.subtract(taxValue);

        return { taxValue: taxValue.getAmount(), netPrice: priceWithoutTax.getAmount() };
    }

    public deductTax(
        price: number,
        taxRate: number,
        currency: CurrencyCode = Dinero.defaultCurrency as CurrencyCode
    ): number {
        this.assertInteger(price, 'Price');
        const priceObject = Dinero({ amount: price, currency });
        const fek = 100 / (taxRate + 100);
        return priceObject.multiply(fek).getAmount();

    }

    public getDefaultCurrency() {
        return CurrencyCode[Dinero.defaultCurrency as ExistingCurrency];
    }

    public sumPrices(
        price: number,
        priceToAdd: number,
        { currencyCode, precision }: Currency,
        { allowsNegativeAddends = false }: { allowsNegativeAddends?: boolean } = {}): number {
        this.assertInteger(priceToAdd, 'Price to sum with');
        this.assertInteger(price, 'Price');

        if (!allowsNegativeAddends) {
            this.assertPositiveNumber(price, 'Price');
            this.assertPositiveNumber(priceToAdd, 'Price to add');
        }

        this.assertSupportedCurrency(currencyCode);

        const currentPrice = Dinero({ amount: price, currency: currencyCode, precision });
        const calculatedPrice = currentPrice.add(Dinero({ amount: priceToAdd, currency: currencyCode, precision }));

        return calculatedPrice.getAmount();
    }
}

export default PriceService;
